import { useState, useEffect } from "react";
import { AiOutlineArrowUp, AiOutlineArrowDown } from "react-icons/ai";
import { MdOutlineNavigateNext, MdOutlineNavigateBefore } from "react-icons/md";
import { Textbox } from "./textbox";
import { Status } from "../constants/status";
import { ReservationsTableColumns } from "../constants/reservationsTableColumns";
import { ExportButton } from "./exportButton";
import { TableName } from "../constants/tableName";

export const TableListStatus = ({
  list,
  type,
  handlePopupDialogOpen,
  isFilter = false,
  rowsPerPage = 10,
}) => {
  const [dataList, setDataList] = useState([]);
  const [filteredDataList, setFilteredDataList] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [searchKeyword, setSearchKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [toggleType, setToggleType] = useState(Status.ACTIVE);
  const totalPages = Math.ceil(filteredDataList.length / rowsPerPage);
  const startRow = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredDataList.slice(
    startRow,
    startRow + rowsPerPage
  );
  const applyFilters = () => {
    setFilteredDataList(
      dataList.filter((data) => {
        const matchesStatus = data.status === toggleType;
        const matchesSearch = Object.keys(data).some((key) => {
          const value = data[key];
          return (
            value &&
            value.toString().toLowerCase().includes(searchKeyword.toLowerCase())
          );
        });
        return matchesStatus && matchesSearch;
      })
    );
  };
  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    const sortedList = [...filteredDataList].sort((a, b) => {
      if (a[key] < b[key]) {
        return direction === "ascending" ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
    setFilteredDataList(sortedList);
    setSortConfig({ key, direction });
  };
  const handleToggle = (value) => {
    setToggleType(value);
  };
  const renderSortIcon = (key) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        return <AiOutlineArrowUp />;
      } else {
        return <AiOutlineArrowDown />;
      }
    }
    return null;
  };
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  const renderPagination = () => {
    const maxVisiblePages = 5;
    const pageNumbers = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    if (startPage > 1) {
      pageNumbers.push(
        <button key={1} type="button" onClick={() => handlePageChange(1)}>
          1
        </button>
      );
      if (startPage > 2) {
        pageNumbers.push(<span key="start-ellipsis">...</span>);
      }
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          type="button"
          onClick={() => handlePageChange(i)}
          className={currentPage === i ? "active" : ""}
        >
          {i}
        </button>
      );
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(<span key="end-ellipsis">...</span>);
      }
      pageNumbers.push(
        <button
          key={totalPages}
          type="button"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }
    return pageNumbers;
  };
  useEffect(() => {
    setDataList(
      [...list].sort((a, b) => {
        const dateA = new Date(a.reservationDate);
        const dateB = new Date(b.reservationDate);
        return dateB - dateA;
      })
    );
  }, [list]);
  useEffect(() => {
    applyFilters();
  }, [searchKeyword, dataList, toggleType]);
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredDataList]);

  return (
    <div>
      {isFilter && (
        <Textbox
          isMandatory={false}
          isPlaceholderOnly={false}
          name="searchKeyword"
          type="text"
          placeholder="Search"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
        />
      )}
      <hr />
      <div className="header-container">
        <div
          className={`header ${
            toggleType === Status.ACTIVE ? "selected-header" : ""
          }`}
          onClick={() => handleToggle(Status.ACTIVE)}
        >
          <label className="header-label">ACTIVE</label>
        </div>
        <div className="vertical-divider"></div>
        <div
          className={`header ${
            toggleType === Status.COMPLETED ? "selected-header" : ""
          }`}
          onClick={() => handleToggle(Status.COMPLETED)}
        >
          <label className="header-label">COMPLETED</label>
        </div>
        <div className="vertical-divider"></div>
        <div
          className={`header ${
            toggleType === Status.CANCELLED ? "selected-header" : ""
          }`}
          onClick={() => handleToggle(Status.CANCELLED)}
        >
          <label className="header-label">CANCELLED</label>
        </div>
      </div>
      <hr />
      <div className="web-details">
        <ExportButton
          fileName={`${toggleType}_${
            type === TableName.NORMALRESERVATIONS ? "normal" : "event"
          }_reservation_list`}
          list={filteredDataList}
        />
      </div>
      <br />
      <br />
      <table className="table-list">
        <thead>
          <tr>
            <th>NO</th>
            {ReservationsTableColumns.map((column) => (
              <th
                className="clickable-text"
                onClick={() => handleSort(column.key)}
                key={column.key}
              >
                {column.label}
                {renderSortIcon(column.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedData.length === 0 ? (
            <tr>
              <td colSpan={ReservationsTableColumns.length + 1}>
                No Records Found
              </td>
            </tr>
          ) : (
            paginatedData.map((data, index) => (
              <tr key={data.id ?? index}>
                <td>{startRow + index + 1}</td>
                {ReservationsTableColumns.map((column) => (
                  <td key={column.key}>
                    {column.key === "id" ? (
                      <div
                        onClick={() => handlePopupDialogOpen(data)}
                        className="clickable-text"
                      >
                        {data[column.key] ?? "N/A"}
                      </div>
                    ) : column.key === "reservationDate" ? (
                      <div
                        className={
                          new Date(
                            new Date(data[column.key]).setHours(0, 0, 0, 0)
                          ) >= new Date(new Date().setHours(0, 0, 0, 0))
                            ? "green-text"
                            : "red-text"
                        }
                      >
                        {data[column.key]}
                      </div>
                    ) : column.key === "status" ? (
                      <div
                        className={
                          data[column.key] === Status.ACTIVE
                            ? "green-text"
                            : data[column.key] === Status.COMPLETED
                            ? "orange-text"
                            : "red-text"
                        }
                      >
                        {data[column.key]}
                      </div>
                    ) : column.key === "addOn" ? (
                      <div>{data[column.key]?.slotTitle ?? "No Add-On"}</div>
                    ) : (
                      data[column.key]
                    )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className="pagination-controls">
        <button
          type="button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <MdOutlineNavigateBefore />
        </button>
        {renderPagination()}
        <button
          type="button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <MdOutlineNavigateNext />
        </button>
      </div>
    </div>
  );
};
